import React, { useState } from "react"
import AddToCollection from "@love-irish/resource-add-to-collection"
import ResourceMenu from "@love-irish/resource-menu"
import ShareModal from "@love-irish/resource-share-modal"
import { UpsellModal } from "@love-irish/upsell-modal"
import { useSaveToggle } from "@love-irish/resource-use-save-toggle"
import { useUsingToggle } from "@love-irish/resource-use-using-toggle"
import {
  IconShare,
  IconLink,
  IconStar,
  IconStarFilled,
  IconUserPlus,
  IconMarkdown,
  IconTools,
} from "@tabler/icons-react"
import { showsUsingToggle } from "./using_toggle_gate"

export { showsUsingToggle } from "./using_toggle_gate"

export interface ResourceActionsProps {
  item: {
    gid?: string
    id?: string | number
    type?: string
    __typename?: string
    saved?: boolean
    /** Whether the current profile is already "using" this resource — initial state for the toggle. */
    currentProfileUsing?: boolean
    path?: string | null
    markdownPath?: string | null
    name?: string | null
    [key: string]: unknown
  }
  currentUser?: unknown
}

/**
 * The action menu for a resource list item: save (star), "using this" (tools), add-to-collection, share
 * (modal), and a copy-link overflow entry. Wraps the shared ResourceMenu so every resource row exposes the
 * same actions from one place. Save + using are the same ProfileEngagement toggle, different engagement type.
 */
export const ResourceActions: React.FC<ResourceActionsProps> = ({ item }) => {
  const [isShareOpen, setIsShareOpen] = useState(false)
  const [isSaveUpsellOpen, setIsSaveUpsellOpen] = useState(false)
  const [isUsingUpsellOpen, setIsUsingUpsellOpen] = useState(false)

  // Clean plain-text Markdown alternate (for AI/LLMs), served by MarkdownRenderable at `${path}.md`.
  // encodeURI so accented fada slugs (e.g. /codex/béarla) don't break the link.
  const markdownUrl = item.path ? encodeURI(`${item.path}.md`) : undefined

  const resourceType = item.type || ""
  const resourceId = item.gid || String(item.id ?? "")

  const {
    saved,
    loading: saveLoading,
    toggle: toggleSave,
  } = useSaveToggle({
    resourceType,
    resourceId,
    name: item.name || undefined,
    sanitizedName: item.sanitizedName || undefined,
    initialSaved: !!item.saved,
    onRequireAuth: () => setIsSaveUpsellOpen(true),
  })

  // Same ProfileEngagement toggle mutation as save, with engagementType "using". Optimistic state + toasts
  // live in the hook; here we just wire it to a menu item and its logged-out upsell.
  const {
    using,
    loading: usingLoading,
    toggle: toggleUsing,
  } = useUsingToggle({
    resourceType,
    resourceId,
    name: item.name || undefined,
    sanitizedName: item.sanitizedName || undefined,
    initialUsing: !!item.currentProfileUsing,
    onRequireAuth: () => setIsUsingUpsellOpen(true),
  })

  const shareUrl =
    typeof window !== "undefined" && item.path
      ? `${window.location.origin}${item.path}`
      : item.path || ""

  const copyLink = () => {
    if (shareUrl) navigator.clipboard?.writeText(shareUrl)
  }

  return (
    <>
      <ResourceMenu>
        <ResourceMenu.Item
          icon={IconStar}
          activeIcon={IconStarFilled}
          active={saved}
          label={saved ? "Saved" : "Save"}
          onClick={toggleSave}
          disabled={saveLoading}
        />
        {showsUsingToggle(item.type, item.__typename) && (
          <ResourceMenu.Item
            icon={IconTools}
            active={using}
            label={
              using
                ? "Unmark that you use this resource"
                : "Mark that you use this resource"
            }
            description="Help people find useful resources"
            onClick={toggleUsing}
            disabled={usingLoading}
          />
        )}
        <ResourceMenu.Item label="Add to collection">
          <AddToCollection itemId={item.gid} align="end" />
        </ResourceMenu.Item>
        <ResourceMenu.Item
          icon={IconShare}
          label="Share"
          onClick={() => setIsShareOpen(true)}
        />
        <ResourceMenu.MenuItem
          icon={IconLink}
          label="Copy link"
          onClick={copyLink}
        />
        {markdownUrl && (
          <ResourceMenu.MenuItem
            icon={IconMarkdown}
            label="View as Markdown [.md]"
            href={markdownUrl}
            newTab
          />
        )}
      </ResourceMenu>

      <ShareModal
        open={isShareOpen}
        onOpenChange={setIsShareOpen}
        url={shareUrl}
        title={item.name || undefined}
      />

      <UpsellModal
        isOpen={isSaveUpsellOpen}
        onClose={() => setIsSaveUpsellOpen(false)}
        featureName="saved_resources"
        analyticsProperties={{ resource_type: item.type }}
        headerGradient="bg-linear-to-br from-warning-400 to-warning-600"
        icon={<IconStarFilled size={32} />}
        title="Save this for later"
        description="Create a free account to save records to your library and build your own collections."
        primaryCta={{
          label: "Create an account",
          href: "/signup",
          icon: IconUserPlus,
        }}
        secondaryCta={{ label: "Login", href: "/login" }}
      />

      <UpsellModal
        isOpen={isUsingUpsellOpen}
        onClose={() => setIsUsingUpsellOpen(false)}
        featureName="using_resources"
        analyticsProperties={{ resource_type: item.type }}
        headerGradient="bg-linear-to-br from-teal-400 to-teal-600"
        icon={<IconTools size={32} />}
        title="Track what you're using"
        description="Create a free account to mark what you're using and show it to the community."
        primaryCta={{
          label: "Create an account",
          href: "/signup",
          icon: IconUserPlus,
        }}
        secondaryCta={{ label: "Login", href: "/login" }}
      />
    </>
  )
}

export default ResourceActions
